import type { SupabaseClient } from '../client'
import { SupabaseError } from '../errors'
import { unwrapList } from './_helpers'

/**
 * O sino do cliente (ADR-084).
 *
 * Até aqui o portal não tinha motor de aviso nenhum: `project_preferences.
 * notification_frequency` existia desde o começo e nenhuma linha de código a
 * lia. O cliente relatava, a equipe resolvia, e ele só descobria se voltasse à
 * tela por conta própria.
 *
 * A leitura é direta na tabela (a RLS limita o cliente às linhas dele) e a
 * escrita passa por RPC: com UPDATE aberto ele poderia reescrever o título do
 * próprio aviso, e o que a tela precisa é de um gesto.
 */

export type AvisoDoPortal = {
  id: string
  project_id: number
  initiative_id: number | null
  bug_report_id: number | null
  type:
    | 'relato_triado'
    | 'relato_entregue'
    | 'relato_mensagem'
    // o Progresso do cliente (20261022200000): pedido novo, cobrança e publicação
    | 'progresso_pedido'
    | 'progresso_cobranca'
    | 'progresso_publicado'
  title: string
  body: string | null
  read: boolean
  read_at: string | null
  created_at: string
}

const COLUNAS =
  'id, project_id, initiative_id, bug_report_id, type, title, body, read, read_at, created_at'

/** Os avisos do cliente, mais recentes primeiro. */
export async function listarAvisos(
  client: SupabaseClient,
  opts: { limite?: number } = {},
): Promise<AvisoDoPortal[]> {
  const query = client
    .schema('portal')
    .from('notifications')
    .select(COLUNAS)
    .order('created_at', { ascending: false })
    .limit(opts.limite ?? 30)

  return unwrapList(await query) as unknown as AvisoDoPortal[]
}

export async function marcarAvisoLido(client: SupabaseClient, id: string): Promise<number> {
  const { data, error } = await client.schema('portal').rpc('marcar_aviso_lido', { p_id: id })
  if (error) throw new SupabaseError(error.message, error)
  return (data as number) ?? 0
}

export async function marcarAvisosLidos(client: SupabaseClient): Promise<number> {
  const { data, error } = await client.schema('portal').rpc('marcar_avisos_lidos')
  if (error) throw new SupabaseError(error.message, error)
  return (data as number) ?? 0
}

/**
 * A conversa do chamado que ainda não virou chamado de garantia: o relato.
 * Mesma forma de `listarMensagensChamado`, outro alvo.
 */
export type MensagemDoRelato = {
  id: string
  texto: string
  autor_nome: string
  autor_lado: 'equipe' | 'cliente'
  created_at: string
  /** Prints da fala, no mesmo formato do relato (ADR-085). */
  anexos: string[]
}

export async function listarMensagensDoRelato(
  client: SupabaseClient,
  bugReportId: number,
): Promise<MensagemDoRelato[]> {
  const { data, error } = await client
    .schema('portal')
    .rpc('listar_mensagens_relato', { p_bug_report_id: bugReportId })
  if (error) throw new SupabaseError(error.message, error)
  return (data as MensagemDoRelato[]) ?? []
}

/**
 * O cliente escreve no próprio relato.
 *
 * Passa pela RPC `escrever_no_relato` (ADR-085), e não por INSERT direto: a
 * tabela só tem policy de escrita para a equipe, então o insert do cliente era
 * recusado pela RLS. A RPC é `SECURITY DEFINER` e confere o acesso ao relato
 * antes de gravar, do mesmo jeito que o aceite e a recusa já fazem.
 */
export async function escreverNoRelato(
  client: SupabaseClient,
  input: { bugReportId: number; texto: string; anexos?: string[]; clientUserId?: string },
): Promise<void> {
  const portal = client.schema('portal') as unknown as {
    rpc: (
      nome: string,
      args: Record<string, unknown>,
    ) => Promise<{ error: { message: string } | null }>
  }
  const { error } = await portal.rpc('escrever_no_relato', {
    p_relato_id: input.bugReportId,
    p_texto: input.texto,
    p_anexos: input.anexos ?? [],
  })
  if (error) throw new SupabaseError(error.message, error)
}
